// Motori per gli "specchietti" (scope) dell'editor BLACKWAVE: generano la forma
// d'onda del modulator e la forma dei due EG, da disegnare affianco al pannello.
//
// Gli ALGORITMI sono un porting 1:1 di quelli usati nei due doc interattivi del
// firmware (BLACKWAVE/docs/modulator_morph.html e adsr_compare.html), a loro volta
// port delle classi C++ lib/Modulator/Modulator.cpp e lib/ADSRv2/ADSRv2.cpp.
// Le tabelle numeriche ADSR arrivano da scopeData.js (generato da
// BLACKWAVE/tools/gen_editor_scopes.js); la LUT seno del modulator e' rigenerata
// qui (-cos q15, come lib/Modulator/tables.h).
//
// >>> A ogni modifica dell'algoritmo Modulator/ADSRv2 nel firmware, riallineare
//     questo file (e rigenerare scopeData.js). Vedi nota in BLACKWAVE/CLAUDE.md. <<<

import { ADSR_ATTACK_257, ADSR_DECAY_257, ADSR_TIMECV_TW } from "./scopeData";

const SR = 31250; // SAMPLE_RATE_ADSR (TC4), come nel firmware

// ===================== MODULATOR (porting da modulator_morph.html) =====================

const MOD_MAXLEVEL = 4095;
const NUM_SOURCES = 11;
const NUM_ZONES = NUM_SOURCES - 1;
const MIN_RATE_HZ = 0.05;
const RATE_OCTAVES = 10;
const EDGE_GAIN = 64;
const PULSE_DUTY = 0.05;
const SH_STEPS_MIN = 2;
const SH_STEPS = 8;
const RND_STEPS = 4;
const SMOOTH_A = 1 / 32;
const NOISE_SEED = 0x585858;
const SRC_PULSE = 7;
const SRC_SAMPLEHOLD = 8;

// LUT seno 1024 entry q15, -cos(2*pi*x) (come gen_modulator_tables.js)
const SINE_MOD = (() => {
  const t = new Int16Array(1024);
  for (let i = 0; i < 1024; i++) t[i] = Math.round(-Math.cos((2 * Math.PI * i) / 1024) * 32767);
  return t;
})();

class Modulator {
  constructor(sampleRate) {
    this.sampleRate = sampleRate;
    this.phase = 0;
    this.stepPhase = 0;
    this.noiseReg = NOISE_SEED;
    this.heldValue = 0;
    this.rndPrev = 0;
    this.rndNext = 0;
    this.smoothFilter = 0;
    this.useSmoothing = true;
    this.useOneShot = false;
    this.oneShotFinished = false;
    this.extAftertouch = -1;
    this.extVelocity = -1;
    this.extEg = -1;
    this.level = Math.floor(MOD_MAXLEVEL / 2);
    this.updateRate(1);
    this.updateWaveformCV(0);
  }

  freqToTuningWord(hz) {
    return Math.floor((4294967295 * hz) / this.sampleRate) >>> 0;
  }

  updateRate(hz) {
    if (hz < 0.001) hz = 0.001;
    this.freqHz = hz;
    this.tw = this.freqToTuningWord(hz);
    this.updateMorphParams();
  }

  updateRateCV(cv) {
    this.updateRate(MIN_RATE_HZ * Math.pow(2, cv * (RATE_OCTAVES / 255)));
  }

  updateWaveformCV(cv) {
    if (cv < 0) cv = 0;
    else if (cv > MOD_MAXLEVEL) cv = MOD_MAXLEVEL;
    this.morphPos = cv * (NUM_ZONES / MOD_MAXLEVEL);
    this.updateMorphParams();
  }

  updateMorphParams() {
    this.zone = Math.floor(this.morphPos);
    if (this.zone >= NUM_ZONES) this.zone = NUM_ZONES - 1;
    this.zfrac = this.morphPos - this.zone;
    this.triApex = 0.5 + 0.5 * this.zfrac;
    this.edgeGain = Math.pow(EDGE_GAIN, this.zfrac);
    this.pulseDuty = 0.5 + (PULSE_DUTY - 0.5) * this.zfrac;

    let stepsPerCycle;
    const everySampleSteps = this.sampleRate / this.freqHz;
    if (this.zone <= SRC_PULSE) {
      stepsPerCycle = SH_STEPS_MIN * Math.pow(SH_STEPS / SH_STEPS_MIN, this.zfrac);
    } else if (this.zone === SRC_SAMPLEHOLD) {
      let ratio = everySampleSteps / SH_STEPS;
      if (ratio < 1) ratio = 1;
      stepsPerCycle = SH_STEPS * Math.pow(ratio, this.zfrac);
    } else {
      let ratio = everySampleSteps / RND_STEPS;
      if (ratio < 1) ratio = 1;
      stepsPerCycle = RND_STEPS * Math.pow(ratio, 1 - this.zfrac);
    }
    this.stepsPerCycle = stepsPerCycle;
    const stepFreq = this.freqHz * stepsPerCycle;
    this.shEverySample = stepFreq >= this.sampleRate * 0.5;
    this.twStep = this.freqToTuningWord(stepFreq);
  }

  setAftertouch(v) {
    this.extAftertouch = Math.min(127, v) * (2 / 127) - 1;
  }
  setVelocity(v) {
    this.extVelocity = Math.min(127, v) * (2 / 127) - 1;
  }
  setEgLevel(l) {
    if (l < 0) l = 0;
    else if (l > MOD_MAXLEVEL) l = MOD_MAXLEVEL;
    this.extEg = l * (2 / MOD_MAXLEVEL) - 1;
  }
  setSmoothing(e) {
    this.useSmoothing = e;
  }
  resetPhase() {
    this.phase = 0;
    this.stepPhase = 0;
  }

  static clampBipolar(v) {
    return v > 1 ? 1 : v < -1 ? -1 : v;
  }

  randomByte() {
    const r = this.noiseReg & 0xffffff;
    const nb = ((r >>> 16) ^ (r >>> 15) ^ (r >>> 14) ^ (r >>> 9)) & 0xff;
    this.noiseReg = (((r << 8) >>> 0) | nb) & 0xffffff;
    return nb;
  }
  randomBipolar() {
    return this.randomByte() * (2 / 255) - 1;
  }

  sineValue() {
    const idx = this.phase >>> 22;
    const t = ((this.phase >>> 14) & 0xff) / 256;
    const a = SINE_MOD[idx];
    const b = SINE_MOD[(idx + 1) & 1023];
    return (a + (b - a) * t) / 32767;
  }

  triangleValue(p, apex) {
    if (apex >= 0.999 || p < apex) return -1 + (2 * p) / apex;
    return 1 - (2 * (p - apex)) / (1 - apex);
  }

  pulseRamp(p, duty) {
    const split = 1 - duty;
    if (p < split) return (p - split) / split;
    return (p - split) / duty;
  }

  sampleHoldValue() {
    if (this.shEverySample) {
      this.heldValue = this.randomBipolar();
      return this.heldValue;
    }
    const previous = this.stepPhase;
    this.stepPhase = (this.stepPhase + this.twStep) >>> 0;
    if (this.stepPhase < previous) this.heldValue = this.randomBipolar();
    return this.heldValue;
  }

  randomSmoothValue() {
    if (this.shEverySample) {
      this.rndPrev = this.rndNext = this.randomBipolar();
      return this.rndNext;
    }
    const previous = this.stepPhase;
    this.stepPhase = (this.stepPhase + this.twStep) >>> 0;
    if (this.stepPhase < previous) {
      this.rndPrev = this.rndNext;
      this.rndNext = this.randomBipolar();
    }
    const t = this.stepPhase / 4294967296;
    const s = t * t * (3 - 2 * t);
    return this.rndPrev + (this.rndNext - this.rndPrev) * s;
  }

  computeNewValue() {
    if (this.useOneShot && this.oneShotFinished) return this.level;
    const previous = this.phase;
    this.phase = (this.phase + this.tw) >>> 0;
    if (this.useOneShot && this.phase < previous) {
      this.oneShotFinished = true;
      this.phase = 0xffffffff;
    }
    const p = this.phase / 4294967296;
    let v;
    switch (this.zone) {
      case 0:
        v = this.extAftertouch + (this.extVelocity - this.extAftertouch) * this.zfrac;
        break;
      case 1:
        v = this.extVelocity + (this.extEg - this.extVelocity) * this.zfrac;
        break;
      case 2:
        v = this.extEg + (this.sineValue() - this.extEg) * this.zfrac;
        break;
      case 3: {
        const s = this.sineValue();
        v = s + (this.triangleValue(p, 0.5) - s) * this.zfrac;
        break;
      }
      case 4:
        v = this.triangleValue(p, this.triApex);
        break;
      case 5:
        v = Modulator.clampBipolar(this.edgeGain * (2 * p - 1));
        break;
      case 6:
        v = Modulator.clampBipolar(EDGE_GAIN * this.pulseRamp(p, this.pulseDuty));
        break;
      case 7: {
        const pw = Modulator.clampBipolar(EDGE_GAIN * this.pulseRamp(p, PULSE_DUTY));
        v = pw + (this.sampleHoldValue() - pw) * this.zfrac;
        break;
      }
      case 8:
        v = this.sampleHoldValue();
        break;
      default:
        v = this.randomSmoothValue();
        break;
    }
    if (this.useSmoothing) {
      this.smoothFilter += SMOOTH_A * (v - this.smoothFilter);
      v = this.smoothFilter;
    } else {
      this.smoothFilter = v;
    }
    this.level = Math.trunc((v + 1) * 0.5 * MOD_MAXLEVEL + 0.5);
    return this.level;
  }
}

// Genera la forma d'onda del modulator (0..1) su N campioni. sourcePot 0..127 sceglie
// la forma (morph). Rate FISSO: mostra sempre 2 cicli, col ciclo principale centrato
// (mezzo ciclo prima, mezzo dopo) — lo specchietto e' una forma, non un tempo. Lo
// SMOOTHING IIR del firmware e' DISATTIVATO qui, cosi' la forma resta netta (una square
// si vede squadrata, non arrotondata). Sorgenti esterne (AT/VEL/EG) a valori fissi
// rappresentativi. Deterministico (LFSR con seed fisso).
export function modulatorWaveform(sourcePot, N = 1024) {
  const mod = new Modulator(SR);
  mod.setSmoothing(false);
  mod.setAftertouch(96);
  mod.setVelocity(48);
  mod.setEgLevel(3000);
  const CYCLES = 2;
  mod.updateRate((SR * CYCLES) / N); // rate fisso: N campioni = 2 cicli
  mod.updateWaveformCV(Math.min(MOD_MAXLEVEL, Math.round((sourcePot / 127) * MOD_MAXLEVEL)));
  // parte mezzo ciclo prima dello zero: il ciclo principale [1..2] resta centrato
  mod.phase = Math.floor(0.5 * 4294967296) >>> 0;
  const out = new Float32Array(N);
  for (let i = 0; i < N; i++) out[i] = mod.computeNewValue() / MOD_MAXLEVEL;
  return out;
}

// Rate REALE del mod source dal pot MOD_RATE (0..127), mappa esponenziale come il
// firmware (updateRateCV): 0.05..51.2 Hz. Etichetta da mostrare nello scope.
export function modRateLabel(pot) {
  const cv = Math.min(255, Math.max(0, (pot / 127) * 255));
  const hz = MIN_RATE_HZ * Math.pow(2, cv * (RATE_OCTAVES / 255));
  return (hz < 1 ? hz.toFixed(2) : hz < 10 ? hz.toFixed(1) : Math.round(hz)) + " Hz";
}

// ===================== ADSRv2 (porting da adsr_compare.html) =====================

// interpolazione lineare in una tabella 257 entry, argomento u in 0..256 -> 0..1
function lut257(tab, u) {
  if (u <= 0) return tab[0] / 65535;
  if (u >= 256) return tab[256] / 65535;
  const i = Math.floor(u);
  const f = u - i;
  return (tab[i] + (tab[i + 1] - tab[i]) * f) / 65535;
}
const riseNorm = p => lut257(ADSR_ATTACK_257, p * 256); // 0 -> 1 lungo lo stadio
const fallNorm = p => lut257(ADSR_DECAY_257, p * 256); //  1 -> 0 lungo lo stadio

// Tempo REALE di uno stadio A/D/R dal pot (0..127), via tabella ControlLookup di
// ENV.ASM (durate ~0.7ms..11s). Etichetta da mostrare nello scope (ms o s).
export function adsrTimeLabel(pot) {
  const cv = Math.min(255, Math.max(0, pot * 2)); // pot 7bit -> cv firmware (pot<<1)
  const ms = (4294967296 / ADSR_TIMECV_TW[cv] / SR) * 1000;
  if (ms < 1000) return Math.round(ms) + " ms";
  const s = ms / 1000;
  return (s < 10 ? s.toFixed(2) : s.toFixed(1)) + " s";
}

// Genera la forma dell'inviluppo (ampiezza 0..1 su N punti). Il grafico e' diviso in
// 4 PORZIONI UGUALI (1/4 ciascuna): attack | decay | sustain | release. Ogni stage
// vive dentro la sua porzione: il suo tempo (pot 0..127) allarga la transizione da un
// minimo visibile fino a TUTTA la larghezza della porzione; oltre, la porzione resta
// piatta al livello di fine stage. Il sustain occupa 1/4 fisso, piatto al suo livello.
// Le curve usano le LUT esponenziali di ENV.ASM (riseNorm/fallNorm).
export function adsrEnvelope(aPot, dPot, sPot, rPot, N = 320) {
  const lvS = Math.min(1, Math.max(0, sPot / 127));
  const EDGE = 0.03; // frazione minima della porzione: transizione sempre visibile
  const fA = Math.max(EDGE, aPot / 127); // quota di porzione occupata dalla transizione
  const fD = Math.max(EDGE, dPot / 127);
  const fR = Math.max(EDGE, rPot / 127);
  const out = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const x = i / (N - 1); // 0..1 su tutto il grafico
    const q = Math.min(3, Math.floor(x * 4)); // porzione 0..3
    const local = x * 4 - q; // 0..1 dentro la porzione
    let y;
    if (q === 0) {
      y = local < fA ? riseNorm(local / fA) : 1; // attack 0 -> 1
    } else if (q === 1) {
      y = local < fD ? lvS + (1 - lvS) * fallNorm(local / fD) : lvS; // decay 1 -> S
    } else if (q === 2) {
      y = lvS; // sustain (livello fisso, 1/4 del grafico)
    } else {
      y = local < fR ? lvS * fallNorm(local / fR) : 0; // release S -> 0
    }
    out[i] = y;
  }
  return out;
}
